import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Award, CheckCircle2, Circle, Home, Hourglass, Loader2, Lock, LogIn, MessageCircle, Package, Sparkles, TrendingUp, UserPlus, Users } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/button'
import { iconComponent } from '@/components/ui/icon-picker'
import { useOptionalChat } from '@/components/features/chat/ChatProvider'
import { SpaceOfferSale } from '@/components/features/spaces/SpaceOfferSale'
import { SignInInvite } from '@/components/features/spaces/SignInInvite'
import { BadgeArt } from '@/components/features/badges/BadgeChip'
import { actionLabelKey } from '@/components/features/members/gamificationActions'
import { readableCode } from '@/components/features/workflows/triggerBlocks'
import { memberPagePath } from '@/lib/memberPage'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute, type DeniedSpace } from '@/lib/RouteContext'
import { openSignup } from '@/lib/loginIntent'
import { pricingPath, PRICING_PATH } from '@/lib/checkout/pricingPath'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { ROUTES } from '@/lib/routes'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { lockReasonKey, type AccessDenial, type AccessLevel, type LevelDenialProgress, type LockReason } from '@/types/access'

interface SpaceAccessDeniedProps {
  /**
   * The refused space as the menu listed it, or `null` for the generic mode:
   * a space link the menu does not list, so there is no title, icon or reason
   * to show — only that it cannot be opened and where access is sold.
   */
  denied: DeniedSpace | null
}

interface AccessOfferProps {
  kind: 'space' | 'section'
  /** The space's or section's id, for "Fale com a equipe" (F-161b). */
  scopeId?: string | null
  /** Title, icon and description as shown to whoever is kept out; `null` for a link the menu does not list. */
  scope: { title: string; icon?: string | null; description?: string | null } | null
  lockReason?: LockReason | null
  /** What the API says opens it (the 403's `data`, or the section's `denial`). */
  denial?: AccessDenial | null
}

type Cta = { label: string; icon: typeof Lock; onClick: () => void }

/**
 * F-151b: the member's way to the level that opens it: the points (a bar and
 * "faltam N") and the level's activity requirements with this week's count,
 * the same numbers as the profile. A requirement in a space the member cannot
 * open never names it.
 */
function LevelPath({ level, progress }: { level: AccessLevel; progress: LevelDenialProgress }) {
  const { t, i18n } = useTranslation(['common', 'ranking', 'badges'])
  const num = (n: number) => new Intl.NumberFormat(i18n.language).format(n)
  const share = level.minPoints > 0 ? Math.min(100, Math.round((progress.points / level.minPoints) * 100)) : 100
  return (
    <div className="mx-auto mt-5 max-w-xl space-y-4 rounded-xl border border-primary-100 bg-surface/80 p-4 text-left" data-level-path>
      <div>
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="text-gray-700">{t('common:access.offer.levelPoints', { points: num(progress.points) })}</span>
          <span className="whitespace-nowrap font-medium text-gray-900" data-level-points-to-go={progress.pointsToGo}>
            {progress.pointsToGo > 0 ? t('common:access.offer.levelPointsToGo', { points: num(progress.pointsToGo) }) : t('common:access.offer.levelPointsReached')}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-primary-100" role="progressbar" aria-valuemin={0} aria-valuemax={level.minPoints} aria-valuenow={Math.min(progress.points, level.minPoints)}>
          <div className="h-full rounded-full bg-primary-600" style={{ width: `${share}%` }} />
        </div>
      </div>
      {progress.requirements.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('common:access.offer.levelWeek')}</p>
          <p className="mb-2 text-xs text-gray-500">{t('ranking:progress.keepHint')}</p>
          <ul className="space-y-1.5">
            {progress.requirements.map((r, i) => {
              const key = actionLabelKey(r.actionType)
              const action = key ? t(key) : readableCode(r.actionType)
              const ok = r.thisWeek >= r.minCount
              return (
                <li key={`${r.actionType}-${r.spaceId ?? ''}-${i}`} className="flex items-start gap-2 text-sm" data-level-requirement data-done={ok}>
                  {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden /> : <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300" aria-hidden />}
                  <span className={cn('min-w-0 flex-1', ok ? 'text-gray-500' : 'text-gray-800')}>
                    {r.spaceHidden
                      ? t('ranking:progress.inHiddenSpace', { action })
                      : r.spaceName ? t('ranking:progress.inSpace', { action, space: r.spaceName }) : action}
                  </span>
                  <span className={cn('whitespace-nowrap tabular-nums', ok ? 'text-emerald-700' : 'font-medium text-gray-900')}>
                    {t('ranking:progress.count', { done: num(Math.min(r.thisWeek, r.minCount)), goal: num(r.minCount) })}
                  </span>
                  {ok && <span className="sr-only">{t('ranking:progress.done')}</span>}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * The offer of a padlocked space or section (F-161a): its name and
 * description, why it is closed and the one next step that opens it — the
 * plan or product that unlocks it, signing in or up, joining the community,
 * waiting for the approval, or talking to the team. Nothing of the content.
 *
 * When it is sold (the 403's `offers`), the page is a sale: the Store's own
 * cards and checkout (`StoreOfferings`), visitors included, who sign up on
 * the way to paying. A group's space is never sold: it says the team opens it.
 *
 * The step comes from the API's `denial`; whether the person is signed in
 * here comes from the session (a visitor and somebody signed in elsewhere
 * reach the API alike).
 */
export function AccessOffer({ kind, scopeId = null, scope, lockReason = null, denial = null }: AccessOfferProps) {
  const { t } = useTranslation(['common', 'auth'])
  const { navigate } = useRoute()
  const { isAuthenticated, isRoleResolved, activeMember, user, logout } = useAuth()
  const { community } = useCommunity()
  const chat = useOptionalChat()
  const [contacting, setContacting] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)

  const Icon = (scope?.icon && iconComponent(scope.icon)) || Lock
  const name = scope?.title || ''
  const actions = denial?.actions ?? []

  const signIn = () => {
    ;(document.querySelector('[data-login-trigger]') as HTMLButtonElement | null)?.click()
  }

  const seePlans = async () => {
    const ids = denial?.subscriptionGroupIds ?? []
    if (ids.length === 1) {
      /* One plan opens it: straight to that plan, by the slug its link uses. */
      const groups = await Promise.resolve(sdk.checkout.getSubscriptionGroups()).catch(() => [])
      const slug = (Array.isArray(groups) ? groups : []).find((group: { id: string; slug?: string | null }) => group.id === ids[0])?.slug
      navigate(slug ? pricingPath(slug) : `${PRICING_PATH}?plans=${ids[0]}`)
      return
    }
    navigate(ids.length ? `${PRICING_PATH}?plans=${ids.join(',')}` : PRICING_PATH)
  }

  const buy = () => {
    const ids = denial?.productIds ?? []
    navigate(ids.length === 1 ? `${ROUTES.products}${ids[0]}` : '/products')
  }

  /* "Fale com a equipe" (F-161b): the server picks who answers and returns the direct conversation;
     it opens in the chat with the request typed in the composer, not sent. */
  const contactTeam = async () => {
    if (!scopeId || !chat || contacting) return
    setContacting(true)
    setContactError(null)
    try {
      const conversation = await sdk.chat.contactTeam(kind === 'section' ? { sectionId: scopeId } : { spaceId: scopeId })
      chat.showReturnedConversation(conversation, { text: t('common:access.denied.contactTeamDraft', { space: name }) })
    } catch (error) {
      setContactError(apiErrorMessage(error).message || t('common:access.denied.contactTeamFailed'))
    } finally {
      setContacting(false)
    }
  }
  const canContactTeam = denial?.canContactTeam === true && Boolean(scopeId) && Boolean(chat?.enabled)

  const plansCta: Cta = { label: t('common:access.denied.subscribe'), icon: Sparkles, onClick: () => { void seePlans() } }
  const buyCta: Cta = { label: t('common:access.denied.buy'), icon: Package, onClick: buy }
  /* A member of a paid-signup community without a plan is "at the door" (F-34): the API
     answers `joinCommunity`, but what lets them in is a plan, not another signup. */
  const atTheDoor = Boolean(activeMember) && actions.includes('joinCommunity')
  const purchaseCtas = [
    ...(actions.includes('seePlans') || atTheDoor ? [plansCta] : []),
    ...(actions.includes('buy') ? [buyCta] : [])
  ]

  /* Signed in, but not a member of this community (the session is another one's). */
  const outsider = isAuthenticated && isRoleResolved && !activeMember && !user?.isSuperAdmin
  const joinFirst = isAuthenticated && (outsider || (actions.includes('joinCommunity') && !atTheDoor))

  /* What the API offers to open it (the Store's cards); the server sends only what opens it. */
  const offers = denial?.offers ?? null
  const offerPlans = offers?.plans ?? []
  const offeredAddOns = offers?.addOns ?? []
  const offerProducts = offers?.products ?? []
  const offerCount = offerPlans.length + offeredAddOns.length + offerProducts.length
  /* F-151a: a badge opens it. The engagement path comes before the purchase (the purchase follows as "or").
     Only public badges are named; a private one is "um badge especial". A visitor gets the badges too. */
  const denialBadges = denial?.badges ?? []
  const hiddenBadge = denial?.hasHiddenBadge === true
  const byBadge = Boolean(scope) && (denialBadges.length > 0 || hiddenBadge)
    && (denial?.reason === 'badge' || actions.includes('earnBadge') || !isAuthenticated)
  /* F-151b: a level opens it; named before the badge and the purchase. A visitor gets the level, never progress. */
  const denialLevel = denial?.level ?? null
  const levelProgress = isAuthenticated ? denial?.progress ?? null : null
  const byLevel = Boolean(scope) && Boolean(denialLevel)
    && (denial?.reason === 'level' || actions.includes('earnPoints') || !isAuthenticated)
  const selling = Boolean(scope) && denial?.reason !== 'approval' && !joinFirst && !byBadge && !byLevel && offerCount > 0
  const byGroup = denial?.reason === 'group' || lockReason === 'group'


  let title = t(kind === 'section' ? 'common:access.denied.sectionTitle' : 'common:access.denied.title')
  let message = ''
  let primary: Cta | null = null
  let secondary: Cta[] = []
  let StateIcon = Lock
  let backHome = false
  /* "Só para membros": the same sign-in invitation as the sale, under the headline. */
  let inviteSignIn = false

  const signInCta: Cta = { label: t('common:access.denied.signIn'), icon: LogIn, onClick: signIn }
  const signupCta: Cta | null = community?.signupEnabled
    ? { label: t(community.signupRequiresApproval ? 'auth:requestAccess' : 'auth:createAccount'), icon: UserPlus, onClick: openSignup }
    : null

  if (!scope) {
    /* The reason is not known here (a group, an approval, a plan): no "Ver planos" that may have nothing to do with it (T13). */
    title = t('common:access.denied.unlistedTitle')
    message = isAuthenticated ? t('common:access.denied.unlisted') : t('common:access.denied.unlistedSignedOut')
    primary = isAuthenticated ? null : signInCta
    backHome = true
  } else if (denial?.reason === 'approval') {
    StateIcon = Hourglass
    title = t('common:access.denied.approvalTitle')
    message = t('common:access.denied.approval', { space: name })
  } else if (byLevel && denialLevel && !joinFirst) {
    StateIcon = TrendingUp
    title = t('common:access.offer.levelTitle', { level: denialLevel.level, name: denialLevel.name })
    if (!isAuthenticated) {
      message = t('common:access.offer.levelSignedOut', { space: name, level: denialLevel.level })
      primary = signInCta
    } else {
      /* Without progress the gamification is off: the level opens nothing for now (decision 2). */
      message = t(levelProgress ? 'common:access.offer.levelText' : 'common:access.offer.levelTextNoProgress', { space: name })
      if (byBadge) message = `${message} ${t('common:access.offer.levelOrBadge')}`
      if (purchaseCtas.length) message = `${message} ${t('common:access.offer.badgeOrBuy')}`
      if (levelProgress && activeMember?.id) {
        const profile = memberPagePath(activeMember.id)
        primary = { label: t('common:access.offer.levelSeeProgress'), icon: TrendingUp, onClick: () => navigate(profile) }
      }
      secondary = purchaseCtas
    }
  } else if (byBadge && !joinFirst) {
    StateIcon = Award
    const badgeCount = denialBadges.length + (hiddenBadge ? 1 : 0)
    title = denialBadges.length === 1 && !hiddenBadge
      ? t('common:access.offer.badgeTitleOne', { badge: denialBadges[0].name })
      : denialBadges.length === 0
        ? t('common:access.offer.badgeTitleHidden')
        : t('common:access.offer.badgeTitleMany')
    if (!isAuthenticated) {
      message = t('common:access.offer.badgeSignedOut', { space: name })
      primary = signInCta
    } else {
      message = t(badgeCount === 1 ? 'common:access.offer.badgeText' : 'common:access.offer.badgeTextMany', { space: name })
      if (purchaseCtas.length) message = `${message} ${t('common:access.offer.badgeOrBuy')}`
      secondary = purchaseCtas
    }
  } else if (selling) {
    /* Rendered below as the Store's cards. */
  } else if (joinFirst) {
    message = t('common:access.denied.notMember', { space: name })
    primary = {
      label: t('common:access.denied.joinCommunity'),
      icon: UserPlus,
      /* The session belongs to another community: signing up here starts from a clean one. */
      onClick: () => { logout(); openSignup() }
    }
  } else if (!isAuthenticated && byGroup) {
    /* A group's space never names the group; a visitor may be in it, so: sign in. */
    StateIcon = Users
    title = t('common:access.offer.groupTitle')
    message = t('common:access.offer.groupSignedOut')
    primary = signInCta
  } else if (!isAuthenticated && purchaseCtas.length) {
    /* For sale, but no card came (nothing checkout takes): the old way, by the plans or products pages. */
    message = t('common:access.denied.signedOutOffer', { space: name })
    primary = signInCta
    secondary = [...(signupCta ? [signupCta] : []), ...purchaseCtas]
  } else if (!isAuthenticated) {
    /* "Só para membros": creating the account is what opens it; signing in is for who already has one. */
    title = t('common:access.offer.membersTitle')
    message = t(signupCta ? 'common:access.offer.membersText' : 'common:access.offer.membersTextNoSignup', { space: name })
    primary = signupCta ?? signInCta
    inviteSignIn = Boolean(signupCta)
  } else if (purchaseCtas.length) {
    message = t(
      lockReason === 'product' ? 'common:access.denied.byProduct'
        : lockReason === 'subscription' ? 'common:access.denied.bySubscription'
          : 'common:access.denied.byPlan',
      { space: name }
    )
    ;[primary, ...secondary] = purchaseCtas
  } else if (byGroup || actions.includes('askTeam')) {
    /* Opened by the team, never sold (F-161b): explain it, and the chat with the team when someone can answer. */
    StateIcon = byGroup ? Users : MessageCircle
    if (byGroup) {
      title = t('common:access.offer.groupTitle')
      message = t(canContactTeam ? 'common:access.offer.groupText' : 'common:access.offer.groupTextNoChat')
    } else {
      message = t('common:access.denied.askTeam', { space: name })
    }
    if (canContactTeam) {
      primary = { label: t('common:access.denied.contactTeam'), icon: contacting ? Loader2 : MessageCircle, onClick: () => { void contactTeam() } }
    }
  } else if (!denial && lockReason === 'subscription') {
    message = t('common:access.denied.bySubscription', { space: name })
    primary = { label: t('common:access.denied.seePlans'), icon: Sparkles, onClick: () => navigate('/pricing') }
  } else if (!denial && lockReason === 'product') {
    message = t('common:access.denied.byProduct', { space: name })
    primary = { label: t('common:access.denied.seeProducts'), icon: Package, onClick: () => navigate('/products') }
  } else {
    message = t('common:access.denied.generic', { space: name })
    backHome = true
  }

  const reasonKey = scope && denial?.reason !== 'approval' ? lockReasonKey(lockReason) : null
  const PrimaryIcon = primary?.icon

  return (
    <div className="min-h-screen bg-gray-50" data-testid="space-access-denied" data-denial-reason={denial?.reason ?? ''} data-offer={selling ? 'store' : 'panel'}>
      {scope && <PageHeader icon={Icon} label={name} description={scope.description || undefined} />}

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        {/* One block with presence (the Store's Plans band): the sale, or the state and its one next step. */}
        <section
          className={cn(
            'relative isolate mx-auto overflow-hidden rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 via-white to-primary-50 px-4 py-8 sm:px-10 sm:py-12',
            selling ? 'max-w-none' : 'max-w-3xl'
          )}
          aria-labelledby="space-offer-title"
        >
          <span aria-hidden="true" className="pointer-events-none absolute -right-24 -top-28 -z-10 h-80 w-80 rounded-full bg-primary-200/40 blur-3xl" />
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-20 -z-10 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />

          {selling && offers ? (
            <SpaceOfferSale
              space={name}
              reasonLabel={reasonKey ? t(reasonKey) : null}
              offers={offers}
              signedIn={isAuthenticated}
              onSignIn={signIn}
            />
          ) : (
            <div className="mx-auto max-w-2xl text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm">
                <StateIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              {reasonKey && lockReason !== 'signIn' && (
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-primary-700" data-lock-reason={lockReason}>{t(reasonKey)}</p>
              )}
              <h2 id="space-offer-title" className={cn('text-balance text-2xl font-bold text-gray-900 sm:text-3xl', reasonKey && lockReason !== 'signIn' ? 'mt-2' : 'mt-4')} data-testid="space-offer-heading">
                {title}
              </h2>
              <p className="mt-3 text-gray-600">{message}</p>
              {byLevel && denialLevel && levelProgress && <LevelPath level={denialLevel} progress={levelProgress} />}
              {byBadge && (
                <ul className="mx-auto mt-5 flex max-w-xl flex-col gap-2 text-left" data-denial-badges>
                  {denialBadges.map((badge) => (
                    <li key={badge.id} className="flex items-center gap-3 rounded-xl border border-primary-100 bg-surface/80 px-3 py-2" data-denial-badge={badge.id}>
                      <BadgeArt src={badge.imageUrl} size="md" />
                      <span className="min-w-0">
                        <span className="block font-medium text-foreground">{badge.name}</span>
                        {badge.description && <span className="block text-sm text-muted-foreground">{badge.description}</span>}
                      </span>
                    </li>
                  ))}
                  {hiddenBadge && (
                    <li className="flex items-center gap-3 rounded-xl border border-primary-100 bg-surface/80 px-3 py-2" data-denial-hidden-badge>
                      <BadgeArt src={null} size="md" />
                      <span className="font-medium text-foreground">{t('common:access.offer.hiddenBadge')}</span>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {!selling && (
            <div role="alert" className="mx-auto mt-6 max-w-2xl">
              {(primary || secondary.length > 0 || backHome) && (
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
                  {primary && PrimaryIcon && (
                    <Button variant="primary" size="lg" className="w-full sm:w-auto" onClick={primary.onClick} disabled={contacting} data-offer-cta>
                      <PrimaryIcon className={cn('mr-2 h-4 w-4', contacting && 'animate-spin')} />
                      {primary.label}
                    </Button>
                  )}
                  {secondary.map((cta) => {
                    const CtaIcon = cta.icon
                    return (
                      <Button key={cta.label} variant="outline" size="lg" className="w-full bg-white sm:w-auto" onClick={cta.onClick}>
                        <CtaIcon className="mr-2 h-4 w-4" />
                        {cta.label}
                      </Button>
                    )
                  })}
                  {backHome && (
                    <Button variant="outline" size="lg" className="w-full bg-white sm:w-auto" onClick={() => navigate('/')}>
                      <Home className="mr-2 h-4 w-4" />
                      {t('common:access.denied.backHome')}
                    </Button>
                  )}
                </div>
              )}
              {inviteSignIn && <SignInInvite onSignIn={signIn} className="mt-5 justify-center" />}
              {contactError && <p role="status" className="mt-3 text-center text-sm text-red-600" data-contact-team-error>{contactError}</p>}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

/**
 * What a member sees when the API refuses a space with 403 (APP-55), now the
 * offer of F-161a: the name and description come from the 403 (or the menu),
 * the next step from the 403's `denial`.
 */
export function SpaceAccessDenied({ denied }: SpaceAccessDeniedProps) {
  const listed = denied?.space ?? null
  const fromApi = denied?.denial?.space
  const scope = listed || fromApi
    ? {
        title: fromApi?.title ?? listed?.title ?? '',
        icon: fromApi?.icon ?? listed?.icon,
        description: fromApi?.description ?? listed?.description
      }
    : null
  const lockReason = denied?.denial?.lockReason ?? (listed?.locked ? listed.lockReason ?? null : null)

  return <AccessOffer kind="space" scopeId={fromApi?.id ?? listed?.id ?? null} scope={scope} lockReason={lockReason} denial={denied?.denial ?? null} />
}
